import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "@mui/material";
import {
  X,
  Search,
  FileText,
  User as UserIcon,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { ExplanationRequest } from "../../../types/explanations";
import { GetTeachersOutput } from "../../../server/StartupAdmins CRUD/OutputsTypes";
import { getTeachers } from "../../../server/StartupAdmins CRUD/Requests";
import { ApiResponse } from "../../../server";
import { getSchoolClassLabel } from "../../common/utils";

interface AssignTeacherModalProps {
  open: boolean;
  request: ExplanationRequest | any | null;
  onAssign: (teacherId: string) => void;
  onClose: () => void;
}

const AssignTeacherModal: React.FC<AssignTeacherModalProps> = ({
  open,
  request,
  onAssign,
  onClose,
}) => {
  const [teachers, setTeachers] = useState<GetTeachersOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Charger les professeurs disponibles seulement à l'ouverture du modal
  useEffect(() => {
    if (open && teachers.length === 0) {
      const fetchTeachers = async () => {
        try {
          setLoading(true);
          console.log("🔍 [AssignTeacherModal] Chargement des professeurs...");
          const response: ApiResponse<GetTeachersOutput[]> =
            await getTeachers();
          console.log("📨 [AssignTeacherModal] Réponse:", response);

          if (response.status === 200 && Array.isArray(response.body)) {
            setError(null);
            setTeachers(response.body);
            console.log(
              "✅ [AssignTeacherModal] Professeurs chargés:",
              response.body.length,
            );
          } else {
            setError("Erreur lors de la récupération des enseignants.");
            console.error("❌ [AssignTeacherModal] Erreur:", response);
          }
        } catch (err) {
          setError(
            "Une erreur s'est produite lors de la connexion au serveur.",
          );
          console.error("❌ [AssignTeacherModal] Exception:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchTeachers();
    }
  }, [open, teachers.length]);

  // Filtrer les professeurs selon la recherche
  const filteredTeachers = useMemo(() => {
    return teachers.filter((teacher) => {
      const fullName = `${teacher.firstname} ${teacher.lastname}`.toLowerCase();
      return fullName.includes(searchQuery.toLowerCase());
    });
  }, [teachers, searchQuery]);

  // Fonction pour générer les initiales
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleConfirm = () => {
    if (selectedTeacherId) {
      onAssign(selectedTeacherId);
      // Réinitialiser
      setSelectedTeacherId("");
      setSearchQuery("");
    }
  };

  const handleClose = () => {
    setSelectedTeacherId("");
    setSearchQuery("");
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      <div className="bg-white rounded-xl shadow-xl flex flex-col max-h-[90vh] w-full">
        {/* Header */}
        <div className="bg-[#E3B500] p-4 sm:p-6 flex-shrink-0 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <UserIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-white">
                Assigner un professeur
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 p-1 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Informations de la demande */}
        <div className="p-4 sm:p-6 bg-gray-50 border-b flex-shrink-0">
          <div className="flex items-start space-x-3">
            <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-gray-700 mb-1">
                Demande d'explication
              </h3>
              <p className="text-sm text-gray-900 mb-2 line-clamp-2">
                {request?.comment ||
                  request?.description ||
                  "Aucune description"}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                  {getSchoolClassLabel(
                    request?.schoolClass || request?.studentClass,
                  )}
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                  {request?.studentName || "Élève"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 sm:p-6 border-b flex-shrink-0">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-8 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E3B500] focus:border-[#E3B500]"
              placeholder="Rechercher un professeur..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="p-3 sm:p-4 m-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 flex-shrink-0 text-sm sm:text-base">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Content - Liste des professeurs */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-6 sm:p-8">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-4 border-[#E3B500] border-t-transparent"></div>
            </div>
          ) : (
            <div className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-3 flex-shrink-0">
                Professeurs disponibles ({filteredTeachers.length})
              </h3>
              <div
                className="overflow-y-auto pr-2 space-y-2"
                style={{ maxHeight: "400px", minHeight: "200px" }}
              >
                {filteredTeachers.length === 0 ? (
                  <div className="text-center text-gray-500 py-6 sm:py-8">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-sm sm:text-base">
                      {searchQuery
                        ? "Aucun professeur trouvé"
                        : "Aucun professeur disponible"}
                    </p>
                    {searchQuery && (
                      <p className="text-xs sm:text-sm mt-1">
                        Essayez de modifier votre recherche
                      </p>
                    )}
                  </div>
                ) : (
                  filteredTeachers.map((teacher) => {
                    const isSelected = selectedTeacherId === teacher._id;
                    const fullName = `${teacher.firstname} ${teacher.lastname}`;
                    const isAvailable =
                      teacher.availability?.available || false;

                    return (
                      <div
                        key={teacher._id}
                        onClick={() =>
                          setSelectedTeacherId(
                            teacher._id === selectedTeacherId
                              ? ""
                              : teacher._id,
                          )
                        }
                        className={`flex items-center p-3 sm:p-4 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? "bg-[#FFFBEA] border-2 border-[#E3B500]"
                            : "border border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        {/* Avatar */}
                        <div
                          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0 ${
                            teacher.photoId ? "" : "bg-[#E3B500]"
                          }`}
                        >
                          {teacher.photoId ? (
                            <img
                              src={teacher.photoId}
                              alt={fullName}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            getInitials(fullName)
                          )}
                        </div>

                        {/* Info */}
                        <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <p className="font-medium text-gray-900 truncate text-sm sm:text-base">
                              {fullName}
                            </p>
                            {isSelected && (
                              <CheckCircle2 className="w-5 h-5 text-[#E3B500] flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600 truncate mb-2">
                            {teacher.email}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                isAvailable
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {isAvailable ? "Disponible" : "Indisponible"}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t bg-gray-50 rounded-b-xl flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 flex-shrink-0">
          <button
            onClick={handleClose}
            className="w-full sm:w-auto px-4 sm:px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm sm:text-base transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedTeacherId}
            className="w-full sm:w-auto px-4 sm:px-6 py-2 text-white bg-[#E3B500] rounded-lg hover:bg-[#D4A600] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#E3B500] focus:ring-offset-2 text-sm sm:text-base transition-colors"
          >
            Assigner le professeur
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default AssignTeacherModal;
